import React, { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api, resolveApiAssetUrl } from '../lib/api'
import {
  defaultCardV9TimelineSlots,
  defaultCardV9TimelineYears,
  hydrateCardTimelineSlots,
  normalizeCardTimelineItems,
  parseCardTimelineSlotsForSave,
} from '../lib/cardTimeline'
import { publicCardOgShareUrl } from '../lib/publicShareUrls'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import { initKakao } from '../lib/kakao'
import { resizeForProfileUpload } from '../lib/imageResize'
import { CARD_TEMPLATE_OPTIONS, getCardTemplateEditUiConfig, getCardTemplateMeta, splitTags } from '../features/publicPage/registry'
import EasyReconfigureModal, { EasyReconfigureContent } from '../features/reconfigure/EasyReconfigureModal'
import {
  CARD_RECONFIGURE_FEATURES,
  CARD_RECONFIGURE_PURPOSES,
  buildCardReconfigureState,
  getCardPurposeFromTemplate,
  getDefaultCardFeatureValues,
} from '../features/reconfigure/reconfigurePresets'
import { isPublicCardConfigured } from '../features/reconfigure/setupStatus'
import { PublicCardTemplateBody, PublicCardTemplatePreview } from './PublicCard'
import ComplianceTextCheckButton from '../components/ComplianceTextCheckButton'
import { collectMarketingStrings } from '../lib/collectMarketingStrings'
import MenuGuideButton from '../components/MenuGuideButton'
import { EditorStatePage } from '../ui/PageLayout'

const WIZARD_STEP_META = [
  { id: 1, label: '템플릿', short: '1' },
  { id: 2, label: '프로필·로고', short: '2' },
  { id: 3, label: '기본 정보', short: '3' },
  { id: 4, label: '소개·경력·태그', short: '4' },
  { id: 5, label: '활동·상담', short: '5' },
  { id: 6, label: '연락처', short: '6' },
  { id: 7, label: '이미지 카드', short: '7' },
]

/** 서버에 저장된 override가 없으면, 명함에 실제 표시되는 회사명(자동)을 입력란에 보여 준다. */
function hydrateCompanyNameField(apiRow) {
  if (!apiRow) return ''
  const raw = apiRow.companyNameOverride
  if (raw != null && String(raw).trim() !== '') return String(raw).trim()
  return String(apiRow.companyName || '').trim()
}

function getCardReconfigurePurposeTemplateId(purposeId) {
  const purpose = CARD_RECONFIGURE_PURPOSES.find((item) => item.id === purposeId) || CARD_RECONFIGURE_PURPOSES[0]
  return purpose?.templateId || 'CARD_V1'
}

function normalizeCardReconfigureFeaturesForTemplate(features = {}, templateId) {
  const editUi = getCardTemplateEditUiConfig(templateId)
  return {
    ...features,
    showCareerCard: editUi.showCareerFreeEditors && features.showCareerCard === true,
    showFreeTextCard: editUi.showCareerFreeEditors && features.showFreeTextCard === true,
  }
}

function getCardReconfigureFeaturesForTemplate(templateId) {
  const editUi = getCardTemplateEditUiConfig(templateId)
  return CARD_RECONFIGURE_FEATURES.filter((feature) => {
    if ((feature.id === 'showCareerCard' || feature.id === 'showFreeTextCard') && !editUi.showCareerFreeEditors) {
      return false
    }
    return true
  })
}

function getCardWritingItems({ templateId, features = {} }) {
  const editUi = getCardTemplateEditUiConfig(templateId)
  const items = [
    {
      id: 'profile',
      stepId: 2,
      label: editUi.showOrgLogoEditor ? '프로필 사진·조직 로고' : '프로필 사진',
      description: '첫 화면에 보이는 사진을 정리합니다.',
    },
    {
      id: 'basic',
      stepId: 3,
      label: '회사명·직책·주소',
      description: '명함의 기본 정보를 입력합니다.',
    },
    {
      id: 'intro-tags',
      stepId: 4,
      label: editUi.showCardIntroEditor ? '소개글·서비스 태그' : '서비스 태그',
      description: editUi.showCardIntroEditor ? '짧은 소개와 전문 분야를 보여줍니다.' : '전문 분야를 짧게 보여줍니다.',
    },
  ]

  if (editUi.showTimelineYearEditor) {
    items.push({
      id: 'timeline',
      stepId: 4,
      label: '타임라인 연도',
      description: '연도와 짧은 설명을 넣어 신뢰감을 보강합니다.',
    })
  }

  if (features.showCareerCard === true && editUi.showCareerFreeEditors) {
    items.push({
      id: 'career',
      stepId: 4,
      label: '경력 카드',
      description: '경력과 자격을 별도 카드로 보여줍니다.',
    })
  }

  if (features.showFreeTextCard === true && editUi.showCareerFreeEditors) {
    items.push({
      id: 'free-text',
      stepId: 4,
      label: '자유 소개',
      description: '정해진 항목 외 문구를 더 담습니다.',
    })
  }

  if (features.showActivityAreaCard === true) {
    items.push({
      id: 'activity',
      stepId: 5,
      label: '활동·상담 지역',
      description: '상담 가능한 지역을 입력합니다.',
    })
  }

  items.push({
    id: 'contact',
    stepId: 6,
    label: '연락처·SNS',
    description: '카카오 상담 링크와 SNS를 연결합니다.',
  })

  if (editUi.showImageCardEditor) {
    items.push({
      id: 'image-card',
      stepId: 7,
      label: '이미지 카드',
      description: '상단에 넣을 대표 이미지를 올립니다.',
    })
  }

  return items
}

function getFirstCardWritingStepId({ templateId, features = {}, wizardStepIds = [] }) {
  const available = new Set(wizardStepIds)
  const item = getCardWritingItems({ templateId, features })
    .find((writingItem) => available.has(writingItem.stepId))
  return item?.stepId || 2
}

export default function MyPublicCardPage() {
  const navigate = useNavigate()

  const myQuery = useQuery({
    queryKey: ['me', 'public-page'],
    queryFn: async () => {
      const res = await api.get('/users/me/public-page', { __silent: true })
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    }
  })

  const d = myQuery.data

  const origin = (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : ''
  const code = d?.code || ''
  const ogVersion = d?.ogVersion || d?.updatedAt || ''
  const cardUrlPath = code ? `/@${code}` : ''
  const cardSharePath = code ? `/v1/share/card/${code}` : ''
  const absoluteCardUrl = origin && cardUrlPath ? `${origin}${cardUrlPath}` : cardUrlPath
  const absoluteShareUrl = publicCardOgShareUrl(code, ogVersion) || (origin && cardSharePath ? `${origin}${cardSharePath}` : cardSharePath)

  const [showLandingLink, setShowLandingLink] = useState(true)
  const [bioShort, setBioShort] = useState('')
  const [introText, setIntroText] = useState('')
  const [careerText, setCareerText] = useState('')
  const [activityAreaText, setActivityAreaText] = useState('')
  const [freeText, setFreeText] = useState('')
  const [kakaoChatUrl, setKakaoChatUrl] = useState('')
  const [profileImageUrl, setProfileImageUrl] = useState('')
  const [cardOrgLogoUrl, setCardOrgLogoUrl] = useState('')
  const [cardOrgLogoFileId, setCardOrgLogoFileId] = useState('')
  /** 프로필·조직 로고 등 이미지 크게 보기 팝업 */
  const [imagePreview, setImagePreview] = React.useState(null) // { rawUrl, title } | null
  const [companyNameOverride, setCompanyNameOverride] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [addressText, setAddressText] = useState('')
  const [mobileFaxNumber, setMobileFaxNumber] = useState('')
  const [serviceTags, setServiceTags] = useState('')
  const [youtubeUrl, setYoutubeUrl] = useState('')
  const [instagramUrl, setInstagramUrl] = useState('')
  const [blogUrl, setBlogUrl] = useState('')
  const [cardTemplateId, setCardTemplateId] = useState('CARD_V1')
  const [templateSectionOpen, setTemplateSectionOpen] = useState(true)
  const [cardSamplePreviewId, setCardSamplePreviewId] = useState(null)
  const [cardTimelineSlots, setCardTimelineSlots] = useState(() => defaultCardV9TimelineSlots())
  /** 단계별 마법사 vs 한 페이지 */
  const [layoutMode, setLayoutMode] = useState('wizard') // 'wizard' | 'full'
  const [wizardStep, setWizardStep] = useState(1)
  const [reconfigureOpen, setReconfigureOpen] = useState(false)
  const [reconfigurePurpose, setReconfigurePurpose] = useState('basic')
  const [reconfigureFeatures, setReconfigureFeatures] = useState(() => getDefaultCardFeatureValues())
  const [initialSetupDone, setInitialSetupDone] = useState(false)
  /** STEP 탭 옆 배지(템플릿 변경 등) */
  const [stepHints, setStepHints] = useState(() => ({}))
  const prevTemplateIdRef = React.useRef(null)
  const [showCareerCard, setShowCareerCard] = useState(true)
  const [showFreeTextCard, setShowFreeTextCard] = useState(true)
  const [showActivityAreaCard, setShowActivityAreaCard] = useState(true)
  const [imageCardEnabled, setImageCardEnabled] = useState(false)
  const [imageCardFileId, setImageCardFileId] = useState('')
  const [imageCardUrl, setImageCardUrl] = useState('')
  const [wizardPreviewCopied, setWizardPreviewCopied] = useState('')

  React.useEffect(() => {
    if (!d) return
    setShowLandingLink(d.showLandingLink !== false)
    setBioShort(d.bioShort || '')
    setIntroText(d.introText || '')
    setCareerText(d.careerText || '')
    setActivityAreaText(d.activityAreaText || '')
    setFreeText(d.freeText || '')
    setKakaoChatUrl(d.kakaoChatUrl || '')
    setProfileImageUrl(d.profileImageUrl || '')
    setCardOrgLogoUrl(d.cardOrgLogoUrl || '')
    setCardOrgLogoFileId(d.cardOrgLogoFileId || '')
    setCompanyNameOverride(hydrateCompanyNameField(d))
    setJobTitle(d.jobTitle || '')
    setAddressText(d.addressText || '')
    setMobileFaxNumber(d.mobileFaxNumber || '')
    setServiceTags(d.serviceTags || '')
    setYoutubeUrl(d.youtubeUrl || '')
    setInstagramUrl(d.instagramUrl || '')
    setBlogUrl(d.blogUrl || '')
    setCardTemplateId(d.cardTemplateId || 'CARD_V1')
    setCardTimelineSlots(hydrateCardTimelineSlots(d))
    setShowCareerCard(d.showCareerCard !== false)
    setShowFreeTextCard(d.showFreeTextCard !== false)
    setShowActivityAreaCard(d.showActivityAreaCard !== false)
    setImageCardEnabled(d.imageCardEnabled === true)
    setImageCardFileId(d.imageCardFileId || '')
    setImageCardUrl(d.imageCardUrl || '')
  }, [d])

  React.useEffect(() => {
    if (!d) return
    const prev = prevTemplateIdRef.current
    if (prev != null && prev !== cardTemplateId) {
      const cfgOld = getCardTemplateEditUiConfig(prev)
      const cfgNew = getCardTemplateEditUiConfig(cardTemplateId)
      setStepHints((h) => {
        const n = { ...h }
        n[1] = '템플릿이 변경되었습니다. 레이아웃을 확인해 주세요.'
        if (cfgNew.showTimelineYearEditor && !cfgOld.showTimelineYearEditor) {
          n[4] = (n[4] ? `${n[4]} ` : '') + '타임라인 연도·설명 입력이 필요합니다.'
        }
        if (cfgNew.introBlockBanner && !cfgOld.introBlockBanner) {
          n[4] = (n[4] ? `${n[4]} ` : '') + '소개·태그 영역 레이아웃이 바뀌었습니다.'
        }
        if (cfgNew.serviceTagsLabelExtra && !cfgOld.serviceTagsLabelExtra) {
          n[4] = (n[4] ? `${n[4]} ` : '') + '태그 입력 안내를 확인해 주세요.'
        }
        if (cfgNew.activityLabelExtra && !cfgOld.activityLabelExtra) {
          n[5] = (n[5] ? `${n[5]} ` : '') + '활동·상담 문구 표시 방식이 바뀌었습니다.'
        }
        if (cfgNew.profileExtraHint && !cfgOld.profileExtraHint) {
          n[2] = '이 템플릿은 프로필·이미지 카드 구성을 확인해 주세요.'
        }
        if ((cfgNew.profileExtraHint || '').includes('이미지 카드') && !(cfgOld.profileExtraHint || '').includes('이미지 카드')) {
          n[7] = (n[7] ? `${n[7]} ` : '') + '이미지 카드 단계를 확인해 주세요.'
        }
        return n
      })
    }
    prevTemplateIdRef.current = cardTemplateId
  }, [cardTemplateId, d])

  React.useEffect(() => {
    if (!imagePreview) return
    const onKey = (e) => {
      if (e.key === 'Escape') setImagePreview(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [imagePreview])

  const updateMutation = useMutation({
    // 업로드 직후 setState 레이스를 피하기 위해 override 지원
    mutationFn: async (override) => {
      const body = {
        cardEnabled: true,
        landingEnabled: true,
        showLandingLink: override?.showLandingLink ?? showLandingLink,
        bioShort: override?.bioShort ?? bioShort,
        introText: override?.introText ?? introText,
        careerText: override?.careerText ?? careerText,
        activityAreaText: override?.activityAreaText ?? activityAreaText,
        freeText: override?.freeText ?? freeText,
        cardOrgLogoFileId: override?.cardOrgLogoFileId !== undefined ? override.cardOrgLogoFileId : cardOrgLogoFileId,
        companyNameOverride: (() => {
          const v = override?.companyNameOverride ?? companyNameOverride
          const t = String(v || '').trim()
          const auto = String(d?.companyName || '').trim()
          if (t === auto) return ''
          return v
        })(),
        jobTitle: override?.jobTitle ?? jobTitle,
        addressText: override?.addressText ?? addressText,
        mobileFaxNumber: override?.mobileFaxNumber ?? mobileFaxNumber,
        kakaoChatUrl: override?.kakaoChatUrl ?? kakaoChatUrl,
        serviceTags: override?.serviceTags ?? serviceTags,
        cardTemplateId: override?.cardTemplateId ?? cardTemplateId,
        youtubeUrl: override?.youtubeUrl ?? youtubeUrl,
        instagramUrl: override?.instagramUrl ?? instagramUrl,
        blogUrl: override?.blogUrl ?? blogUrl,
        cardTimeline: override?.cardTimeline ?? parseCardTimelineSlotsForSave(cardTimelineSlots),
        showCareerCard: override?.showCareerCard ?? showCareerCard,
        showFreeTextCard: override?.showFreeTextCard ?? showFreeTextCard,
        showActivityAreaCard: override?.showActivityAreaCard ?? showActivityAreaCard,
        imageCardEnabled: override?.imageCardEnabled ?? imageCardEnabled,
        imageCardFileId: override?.imageCardFileId !== undefined ? override.imageCardFileId : imageCardFileId,
      }
      const res = await api.put('/users/me/public-page', body)
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
    onSuccess: (updated) => {
      try { window?.toast?.show?.('모바일명함 설정이 저장되었습니다', 'success') } catch { }
      myQuery.refetch()

      setShowLandingLink(updated?.showLandingLink !== false)
      setBioShort(updated?.bioShort || '')
      setIntroText(updated?.introText || '')
      setCareerText(updated?.careerText || '')
      setActivityAreaText(updated?.activityAreaText || '')
      setFreeText(updated?.freeText || '')
      setKakaoChatUrl(updated?.kakaoChatUrl || '')
      setProfileImageUrl(updated?.profileImageUrl || '')
      setCardOrgLogoUrl(updated?.cardOrgLogoUrl || '')
      setCardOrgLogoFileId(updated?.cardOrgLogoFileId || '')
      setCompanyNameOverride(hydrateCompanyNameField(updated))
      setJobTitle(updated?.jobTitle || '')
      setAddressText(updated?.addressText || '')
      setMobileFaxNumber(updated?.mobileFaxNumber || '')
      setServiceTags(updated?.serviceTags || '')
      setYoutubeUrl(updated?.youtubeUrl || '')
      setInstagramUrl(updated?.instagramUrl || '')
      setBlogUrl(updated?.blogUrl || '')
      setCardTemplateId(updated?.cardTemplateId || 'CARD_V1')
      setCardTimelineSlots(hydrateCardTimelineSlots(updated))
      setShowCareerCard(updated?.showCareerCard !== false)
      setShowFreeTextCard(updated?.showFreeTextCard !== false)
      setShowActivityAreaCard(updated?.showActivityAreaCard !== false)
      setImageCardEnabled(updated?.imageCardEnabled === true)
      setImageCardFileId(updated?.imageCardFileId || '')
      setImageCardUrl(updated?.imageCardUrl || '')
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || e?.message || '저장 실패'
      try { window?.toast?.show?.(msg, 'error') } catch { }
    }
  })

  const uploadOrgLogoMutation = useMutation({
    mutationFn: async (file) => {
      if (!file) throw new Error('no_file')
      const resized = await resizeForProfileUpload(file, {
        maxSide: 1024,
        quality: 0.82,
        preferWebp: true,
        maxBytes: 3 * 1024 * 1024,
      })
      const fd = new FormData()
      fd.append('file', resized)
      const up = await api.post('/files/upload/public-card-org-logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      const upData = (up?.data && up.data.data !== undefined) ? up.data.data : up.data
      const fileId = upData?.fileId
      if (!fileId) throw new Error('upload_failed')
      return { fileId }
    },
    onSuccess: ({ fileId }) => {
      const next = fileId || ''
      setCardOrgLogoFileId(next)
      updateMutation.mutate({ cardOrgLogoFileId: next })
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || e?.message || '조직 로고 업로드 실패'
      try { window?.toast?.show?.(msg, 'error') } catch { }
    }
  })

  const uploadImageCardMutation = useMutation({
    mutationFn: async (file) => {
      if (!file) throw new Error('no_file')
      const resized = await resizeForProfileUpload(file, {
        maxSide: 1920,
        quality: 0.85,
        preferWebp: true,
        maxBytes: 9 * 1024 * 1024,
      })
      const fd = new FormData()
      fd.append('file', resized)
      const up = await api.post('/files/upload/public-landing-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      const upData = (up?.data && up.data.data !== undefined) ? up.data.data : up.data
      const fileId = upData?.fileId
      if (!fileId) throw new Error('upload_failed')
      return { fileId }
    },
    onSuccess: ({ fileId }) => {
      const next = fileId || ''
      setImageCardFileId(next)
      setImageCardEnabled(true)
      updateMutation.mutate({ imageCardFileId: next, imageCardEnabled: true })
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || e?.message || '이미지 카드 업로드 실패'
      try { window?.toast?.show?.(msg, 'error') } catch { }
    },
  })

  const orgChainDisplay = useMemo(() => {
    if (!d) return ''
    return [d.orgRootName, d.orgParentName, d.orgName].filter(Boolean).join(' · ')
  }, [d])

  const displayCompanyOnCard = useMemo(() => {
    const typed = String(companyNameOverride || '').trim()
    return typed || String(d?.companyName || '').trim() || '—'
  }, [companyNameOverride, d?.companyName])

  const displayJobOnCard = useMemo(() => {
    const typed = String(jobTitle || '').trim()
    return typed || String(d?.jobTitle || d?.title || '').trim() || '팀장'
  }, [jobTitle, d?.jobTitle, d?.title])

  const displayAddressOnCard = useMemo(() => {
    const typed = String(addressText || '').trim()
    return typed || '—'
  }, [addressText])

  const cardEditUi = useMemo(() => getCardTemplateEditUiConfig(cardTemplateId), [cardTemplateId])

  const wizardStepDisplayMeta = useMemo(
    () => WIZARD_STEP_META.map((s) => {
      if (s.id === 2 && !cardEditUi.showOrgLogoEditor) return { ...s, label: '프로필' }
      if (s.id === 4) {
        if (!cardEditUi.showCardIntroEditor && !cardEditUi.showCareerFreeEditors) return { ...s, label: '태그' }
        if (cardEditUi.showCareerFreeEditors && (showCareerCard || showFreeTextCard)) return { ...s, label: '소개·경력·태그' }
        return { ...s, label: '소개·태그' }
      }
      return s
    }),
    [
      cardEditUi.showOrgLogoEditor,
      cardEditUi.showCardIntroEditor,
      cardEditUi.showCareerFreeEditors,
      showCareerCard,
      showFreeTextCard,
    ],
  )

  /** 마법사: 이미지 카드 단계(7)는 허브 템플릿에서만 노출 */
  const wizardStepIds = useMemo(() => {
    const ids = [1, 2, 3, 4]
    if (showActivityAreaCard !== false) ids.push(5)
    ids.push(6)
    if (cardEditUi.showImageCardEditor) ids.push(7)
    return ids
  }, [cardEditUi.showImageCardEditor, showActivityAreaCard])

  const reconfigurePurposeTemplateId = useMemo(
    () => getCardReconfigurePurposeTemplateId(reconfigurePurpose),
    [reconfigurePurpose],
  )

  const reconfigureVisibleFeatures = useMemo(
    () => getCardReconfigureFeaturesForTemplate(reconfigurePurposeTemplateId),
    [reconfigurePurposeTemplateId],
  )

  const normalizedReconfigureFeatures = useMemo(
    () => normalizeCardReconfigureFeaturesForTemplate(reconfigureFeatures, reconfigurePurposeTemplateId),
    [reconfigureFeatures, reconfigurePurposeTemplateId],
  )

  const reconfigureWritingItems = useMemo(
    () => getCardWritingItems({
      templateId: reconfigurePurposeTemplateId,
      features: normalizedReconfigureFeatures,
    }),
    [normalizedReconfigureFeatures, reconfigurePurposeTemplateId],
  )

  const reconfigureSelectedFeatureLabels = useMemo(
    () => reconfigureVisibleFeatures
      .filter((feature) => feature.required || normalizedReconfigureFeatures[feature.id] === true)
      .map((feature) => feature.label),
    [normalizedReconfigureFeatures, reconfigureVisibleFeatures],
  )

  React.useEffect(() => {
    setWizardStep((ws) => (wizardStepIds.includes(ws) ? ws : wizardStepIds[0]))
  }, [wizardStepIds])

  const canShare = Boolean(code && absoluteCardUrl && absoluteShareUrl)

  const smsShareHref = useMemo(() => {
    const name = d?.name || ''
    const isIOS = (typeof navigator !== 'undefined') ? /iPad|iPhone|iPod/.test(navigator.userAgent || '') : false
    const sep = isIOS ? '&' : '?'
    if (!canShare) return ''
    return `sms:${sep}body=${encodeURIComponent(`${name} 명함: ${absoluteShareUrl}`)}`
  }, [absoluteShareUrl, canShare, d?.name])

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text) } catch { }
    try { window?.toast?.show?.('복사되었습니다', 'success') } catch { }
  }

  const openReconfigure = React.useCallback(() => {
    setReconfigurePurpose(getCardPurposeFromTemplate(cardTemplateId))
    setReconfigureFeatures(getDefaultCardFeatureValues({
      showLandingLink,
      showCareerCard,
      showActivityAreaCard,
      showFreeTextCard,
    }))
    setReconfigureOpen(true)
  }, [
    cardTemplateId,
    showActivityAreaCard,
    showCareerCard,
    showFreeTextCard,
    showLandingLink,
  ])

  const applyReconfigure = React.useCallback(() => {
    const next = buildCardReconfigureState({
      purposeId: reconfigurePurpose,
      features: normalizedReconfigureFeatures,
    })
    const nextEditUi = getCardTemplateEditUiConfig(next.cardTemplateId)
    const nextWizardStepIds = [1, 2, 3, 4, 5, 6]
    if (nextEditUi.showImageCardEditor) nextWizardStepIds.push(7)
    setCardTemplateId(next.cardTemplateId)
    setShowLandingLink(next.showLandingLink)
    setShowCareerCard(next.showCareerCard)
    setShowActivityAreaCard(next.showActivityAreaCard)
    setShowFreeTextCard(next.showFreeTextCard)
    setLayoutMode('wizard')
    setWizardStep(getFirstCardWritingStepId({
      templateId: next.cardTemplateId,
      features: normalizedReconfigureFeatures,
      wizardStepIds: nextWizardStepIds,
    }))
    setInitialSetupDone(true)
    setReconfigureOpen(false)
    try { window?.toast?.show?.('명함 구성을 적용했습니다. 저장하면 고객에게 반영됩니다.', 'success') } catch { }
  }, [normalizedReconfigureFeatures, reconfigurePurpose])

  /** 미리보기·링크 — 헤더 타이틀 옆 (개인홈페이지 편집과 동일 톤) */
  const shareActionBtnClass =
    'inline-flex min-h-[36px] min-w-[4.5rem] flex-1 items-center justify-center whitespace-nowrap rounded-lg border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 sm:min-w-0 sm:flex-none sm:px-3 sm:text-sm'

  const goWizardStep = React.useCallback((id) => {
    if (!wizardStepIds.includes(id)) return
    setWizardStep(id)
    setStepHints((h) => {
      if (!h[id]) return h
      const next = { ...h }
      delete next[id]
      return next
    })
  }, [wizardStepIds])

  const handleWizardSaveAndNext = React.useCallback(async () => {
    try {
      await updateMutation.mutateAsync()
      setWizardStep((step) => {
        const idx = wizardStepIds.indexOf(step)
        if (idx === -1) return wizardStepIds[0]
        if (idx < wizardStepIds.length - 1) {
          const nextId = wizardStepIds[idx + 1]
          setStepHints((h) => {
            if (!h[nextId]) return h
            const n = { ...h }
            delete n[nextId]
            return n
          })
          return nextId
        }
        return step
      })
    } catch {
      /* mutation onError */
    }
  }, [updateMutation, wizardStepIds])

  const previewTemplateProps = useMemo(() => {
    if (!d) return null
    const companyName =
      String(companyNameOverride || '').trim()
      || String(d.companyName || '').trim()
      || [d.orgRootName, d.orgName].filter(Boolean)[0]
      || ''
    const titleEff = String(jobTitle || '').trim() || String(d.jobTitle || d.title || '').trim() || '팀장'
    const addressEff = String(addressText || '').trim() || String(d.addressText || '').trim() || ''
    const mobileFaxEff = String(mobileFaxNumber || '').trim() || String(d.mobileFaxNumber || '').trim() || ''
    const introEff = String(introText || '').trim() || String(bioShort || '').trim() || ''
    const savedTl = parseCardTimelineSlotsForSave(cardTimelineSlots)
    const tlPayload = savedTl.length >= 2
      ? { cardTimeline: savedTl }
      : { cardTimeline: d.cardTimeline, cardTimelineYears: d.cardTimelineYears }
    const normalizedTimeline = normalizeCardTimelineItems({ ...d, ...tlPayload })
    const cardTimelineItemsProp =
      normalizedTimeline && normalizedTimeline.length >= 2
        ? normalizedTimeline
        : defaultCardV9TimelineYears().map((y) => ({ year: y, label: '' }))
    const phone = d.phoneNumber || ''
    const phoneDigits = phone ? phone.replace(/[^0-9+]/g, '') : ''
    const smsHref = phoneDigits ? `sms:${phoneDigits}` : ''
    const originP = (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : ''
    const codeP = d.code || ''
    const cardUrlPathP = codeP ? `/@${codeP}` : ''
    const landingUrlPathP = codeP ? `/p/${codeP}` : ''
    const absoluteCardUrlP = originP && cardUrlPathP ? `${originP}${cardUrlPathP}` : cardUrlPathP
    const absoluteShareUrlP = publicCardOgShareUrl(codeP, d?.ogVersion || d?.updatedAt || '')
    const isIOS = (typeof navigator !== 'undefined') ? /iPad|iPhone|iPod/.test(navigator.userAgent || '') : false
    const smsBodySep = isIOS ? '&' : '?'
    const smsShareHrefP = `sms:${smsBodySep}body=${encodeURIComponent(`${d.name || ''} 명함: ${absoluteShareUrlP}`)}`

    return {
      name: d.name || '',
      title: titleEff,
      companyName,
      org: [d.orgRootName, d.orgParentName, d.orgName].filter(Boolean).join(' · '),
      intro: introEff,
      activity: showActivityAreaCard !== false ? String(activityAreaText || '').trim() : '',
      addressText: addressEff,
      mobileFaxNumber: mobileFaxEff,
      email: d.email || '',
      phone,
      phoneDigits,
      smsHref,
      youtubeUrl,
      instagramUrl,
      blogUrl,
      kakaoChatUrl,
      profileImageUrl,
      tags: splitTags(serviceTags),
      downloadVcard: async () => {},
      isOwner: false,
      absoluteCardUrl: absoluteCardUrlP,
      absoluteShareUrl: absoluteShareUrlP,
      smsShareHref: smsShareHrefP,
      copy: async (t) => {
        try { await navigator.clipboard.writeText(t) } catch { }
        setWizardPreviewCopied(String(t))
        setTimeout(() => setWizardPreviewCopied(''), 900)
      },
      copied: wizardPreviewCopied,
      landingEnabled: true,
      showLandingLink: showLandingLink !== false,
      landingUrlPath: landingUrlPathP,
      cardTimelineItems: cardTimelineItemsProp,
      careerText,
      freeText,
      showCareerCard,
      showFreeTextCard,
      showActivityAreaCard,
      imageCardEnabled: Boolean(imageCardEnabled),
      imageCardUrl: imageCardUrl || '',
      cardOrgLogoUrl: cardOrgLogoUrl || '',
      cardPreviewMode: true,
    }
  }, [
    d,
    companyNameOverride,
    jobTitle,
    addressText,
    mobileFaxNumber,
    introText,
    bioShort,
    activityAreaText,
    careerText,
    freeText,
    serviceTags,
    youtubeUrl,
    instagramUrl,
    blogUrl,
    kakaoChatUrl,
    profileImageUrl,
    cardOrgLogoUrl,
    cardTimelineSlots,
    showLandingLink,
    showCareerCard,
    showFreeTextCard,
    showActivityAreaCard,
    imageCardEnabled,
    imageCardUrl,
    wizardPreviewCopied,
  ])

  const getPublicCardComplianceText = useCallback(() => {
    const timeline = collectMarketingStrings(cardTimelineSlots, 2000)
    const lines = [
      bioShort,
      introText,
      careerText,
      activityAreaText,
      freeText,
      companyNameOverride,
      jobTitle,
      serviceTags,
      addressText,
      mobileFaxNumber,
      timeline,
    ]
    return lines
      .filter((s) => String(s || '').trim())
      .join('\n')
      .slice(0, 4000)
  }, [
    bioShort,
    introText,
    careerText,
    activityAreaText,
    freeText,
    companyNameOverride,
    jobTitle,
    serviceTags,
    addressText,
    mobileFaxNumber,
    cardTimelineSlots,
  ])

  const publicCardStateHeaderProps = {
    title: '모바일명함',
    titleAccessory: <MenuGuideButton slug="public-card" title="모바일명함 가이드" />,
  }

  if (myQuery.isLoading) return (
    <EditorStatePage {...publicCardStateHeaderProps}>
      <div className="rounded-2xl border border-sgez-border-strong bg-sgez-surface shadow-sm">
        <Loading />
      </div>
    </EditorStatePage>
  )
  if (myQuery.isError) return (
    <EditorStatePage {...publicCardStateHeaderProps}>
      <div className="rounded-2xl border border-sgez-border-strong bg-sgez-surface shadow-sm">
        <InlineError message={myQuery.error?.message || '불러오기 실패'} />
      </div>
    </EditorStatePage>
  )
  if (!d) return (
    <EditorStatePage {...publicCardStateHeaderProps}>
      <div className="rounded-2xl border border-sgez-border-strong bg-sgez-surface shadow-sm">
        <InlineError message="데이터가 없습니다." />
      </div>
    </EditorStatePage>
  )

  const reconfigureContentProps = {
    title: '명함 다시 구성하기',
    description: '기존 연락처와 문구는 그대로 두고, 명함의 분위기와 보여줄 기능만 다시 정리합니다.',
    purposeLabel: '명함에서 어떤 인상을 주고 싶나요?',
    purposes: CARD_RECONFIGURE_PURPOSES,
    purposeId: reconfigurePurpose,
    onPurposeIdChange: setReconfigurePurpose,
    features: reconfigureVisibleFeatures,
    featureValues: normalizedReconfigureFeatures,
    onFeatureChange: (id, value) => setReconfigureFeatures((prev) => ({ ...prev, [id]: value })),
    onApply: applyReconfigure,
    applyLabel: '작성 시작',
    note: '적용 후 바로 공개 내용이 바뀌지는 않습니다. 화면에서 미리 확인한 뒤 저장해야 고객에게 반영됩니다.',
    stepMode: true,
    stepLabels: ['인상 선택', '기능 선택', '작성 항목'],
    renderReview: () => (
      <section>
        <div className="text-sm font-bold text-slate-900">이 순서로 작성합니다</div>
        <div className="mt-3 overflow-hidden rounded-3xl border border-slate-200 bg-white">
          {reconfigureWritingItems.map((item, index) => (
            <div
              key={item.id}
              className={`flex gap-3 px-4 py-4 ${index === 0 ? '' : 'border-t border-slate-100'}`}
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-50 text-xs font-black text-brand-navy">
                {index + 1}
              </span>
              <div className="min-w-0">
                <div className="text-sm font-extrabold text-slate-950">{item.label}</div>
                <div className="mt-1 text-xs leading-5 text-slate-500">{item.description}</div>
              </div>
            </div>
          ))}
        </div>
        {reconfigureSelectedFeatureLabels.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {reconfigureSelectedFeatureLabels.map((label) => (
              <span
                key={label}
                className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-700"
              >
                {label}
              </span>
            ))}
          </div>
        ) : null}
      </section>
    ),
    purposeGridClassName: 'mt-3 grid gap-2 sm:grid-cols-2',
    renderPurposeAction: (purpose) => (
      <button
        type="button"
        className="inline-flex h-8 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        onClick={(event) => {
          event.stopPropagation()
          setCardSamplePreviewId(purpose.templateId)
        }}
      >
        샘플보기
      </button>
    ),
  }

  const reconfigureModal = (
    <EasyReconfigureModal
      open={reconfigureOpen}
      {...reconfigureContentProps}
      onClose={() => setReconfigureOpen(false)}
    />
  )

  const cardSamplePreviewModal = cardSamplePreviewId ? (
    <div className="fixed inset-0 z-[110] flex items-start justify-center overflow-y-auto px-3 py-8 sm:py-12">
      <button
        type="button"
        className="fixed inset-0 bg-black/45"
        onClick={() => setCardSamplePreviewId(null)}
        aria-label="샘플 미리보기 닫기"
      />
      <div className="relative z-[1] my-auto w-full max-w-md">
        {(() => {
          const pid = getCardTemplateMeta(cardSamplePreviewId).id
          const immersive = pid === 'CARD_V4' || pid === 'CARD_V6' || pid === 'CARD_V11'
          const panelBg = pid === 'CARD_V6'
            ? 'bg-neutral-950'
            : pid === 'CARD_V4'
              ? 'bg-[#0c2138]'
              : pid === 'CARD_V11'
                ? 'bg-[#020617]'
                : 'bg-slate-100'
          const meta = getCardTemplateMeta(cardSamplePreviewId)
          return (
            <div
              className="max-h-[min(90vh,880px)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl flex flex-col"
              role="dialog"
              aria-modal="true"
              aria-labelledby="card-sample-preview-title"
            >
              <div className="shrink-0 border-b border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div id="card-sample-preview-title" className="text-sm font-bold text-slate-900">
                      샘플 미리보기
                    </div>
                    <div className="mt-0.5 text-xs font-medium text-slate-600">{meta.label}</div>
                    <p className="mt-2 text-[11px] leading-snug text-slate-500">
                      가상의 예시 인물·연락처입니다. 적용 전 레이아웃만 참고하시고, 저장 후 실제 명함에는 본인 정보가 반영됩니다.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    onClick={() => setCardSamplePreviewId(null)}
                  >
                    닫기
                  </button>
                </div>
              </div>
              <div className={`min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 sm:p-4 ${panelBg} ${immersive ? 'text-slate-100' : ''}`}>
                <div className="mx-auto max-w-md pb-2">
                  <PublicCardTemplatePreview templateId={cardSamplePreviewId} />
                </div>
              </div>
            </div>
          )
        })()}
      </div>
    </div>
  ) : null

  if (!initialSetupDone && !isPublicCardConfigured(d)) {
    return (
      <EditorStatePage {...publicCardStateHeaderProps}>
        <div className="mx-auto max-w-3xl py-5">
          <EasyReconfigureContent
            {...reconfigureContentProps}
            title="생성된 모바일명함이 없습니다. 만들어주세요."
            description="질문에 답하면 기본 구성이 먼저 만들어지고, 이후 세부 편집을 이어갈 수 있습니다."
            showCloseButton={false}
            className="shadow-sm"
          />
        </div>
        {cardSamplePreviewModal}
      </EditorStatePage>
    )
  }

  const hid = (step) => (layoutMode === 'wizard' && wizardStep !== step ? 'hidden' : '')
  const previewCardId = getCardTemplateMeta(cardTemplateId).id
  const immersivePreview = previewCardId === 'CARD_V4' || previewCardId === 'CARD_V6' || previewCardId === 'CARD_V11'
  const previewPanelBg = previewCardId === 'CARD_V6'
    ? 'bg-neutral-950'
    : previewCardId === 'CARD_V4'
      ? 'bg-[#0c2138]'
      : previewCardId === 'CARD_V11'
        ? 'bg-[#030712]'
        : 'bg-slate-100'

  return (
    <div className="min-h-full bg-sgez-surface">
      <header className="sgez-editor-header sticky top-0 z-30 border-b border-slate-200 bg-white shadow-sm">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-[1_1_12rem] flex-wrap items-center gap-2">
              <h1 className="text-lg font-bold text-slate-900">모바일 명함 편집</h1>
              <MenuGuideButton slug="public-card" title="모바일명함 가이드" />
              <div className="grid min-w-0 w-full max-w-[14rem] grid-cols-2 gap-1 sm:w-auto sm:max-w-none sm:flex sm:flex-nowrap sm:gap-1.5">
                <button
                  type="button"
                  className={`${shareActionBtnClass} ${canShare ? '' : 'cursor-not-allowed opacity-40'}`}
                  title="카카오·SNS 미리보기용 OG 링크"
                  onClick={() => { if (canShare) copy(absoluteShareUrl) }}
                  disabled={!canShare}
                >
                  링크 복사
                </button>
                <a
                  className={`${shareActionBtnClass} ${canShare ? '' : 'cursor-not-allowed opacity-40'}`}
                  href={canShare ? cardUrlPath : undefined}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => { if (!canShare) e.preventDefault() }}
                >
                  미리보기
                </a>
              </div>
            </div>
            <div className="flex min-w-0 max-w-full flex-[1_1_16rem] flex-wrap items-center justify-end gap-1.5 sm:gap-2">
              <ComplianceTextCheckButton getText={getPublicCardComplianceText} />
              <button
                type="button"
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                onClick={() => navigate(-1)}
              >
                돌아가기
              </button>
              <button
                type="button"
                className="rounded-xl bg-brand-navy px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                disabled={updateMutation.isPending}
                onClick={() => updateMutation.mutate()}
              >
                {updateMutation.isPending ? '저장 중…' : '저장'}
              </button>
            </div>
          </div>
          {!canShare ? (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-900">
              명함 공유 링크가 아직 없으면 미리보기·공유가 제한됩니다. 잠시 후 새로고침해 보세요.
            </p>
          ) : null}
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 pb-10 pt-3">
        <div className={`${layoutMode === 'wizard' ? 'grid gap-8 lg:grid-cols-2' : ''}`}>
          {layoutMode === 'wizard' && previewTemplateProps ? (
            <div className={`hidden space-y-2 lg:sticky lg:top-24 lg:block lg:self-start ${immersivePreview ? 'text-slate-100' : ''}`}>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">실시간 미리보기</div>
              <div className={`rounded-2xl border border-slate-200/80 shadow-xl ring-1 ring-black/5 ${previewPanelBg}`}>
                <div className={`mx-auto max-w-md px-3 py-5 ${immersivePreview ? '' : ''}`}>
                  <PublicCardTemplateBody cardTemplateId={previewCardId} templateProps={previewTemplateProps} />
                </div>
              </div>
            </div>
          ) : null}

          <div className={`space-y-5 ${layoutMode === 'wizard' ? 'min-w-0' : 'mx-auto max-w-3xl'}`}>
            <div className="space-y-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-slate-900">편집 방식</div>
                    <div className="mt-1 text-xs leading-5 text-slate-500">
                      처음부터 다시 구성하거나 모든 항목을 한 번에 편집할 수 있습니다.
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:flex sm:shrink-0 sm:flex-wrap sm:justify-end">
                    <button
                      type="button"
                      className="rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-bold text-orange-800 hover:bg-orange-100"
                      onClick={openReconfigure}
                    >
                      다시 처음부터 구성하기
                    </button>
                    <button
                      type="button"
                      className={`rounded-xl border px-3 py-2 text-xs font-bold ${
                        layoutMode === 'full'
                          ? 'border-brand-navy bg-sky-50 text-brand-navy'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                      onClick={() => setLayoutMode('full')}
                    >
                      고급편집
                    </button>
                    {layoutMode === 'full' ? (
                      <button
                        type="button"
                        className="col-span-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 sm:col-span-1"
                        onClick={() => setLayoutMode('wizard')}
                      >
                        STEP 편집
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
              {layoutMode === 'wizard' ? (
                <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
                  <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">STEP</div>
                  <div className="flex flex-wrap gap-1">
                    {wizardStepDisplayMeta.filter((s) => wizardStepIds.includes(s.id)).map((s) => {
                      const stepNum = wizardStepIds.indexOf(s.id) + 1
                      return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => goWizardStep(s.id)}
                        title={`STEP ${stepNum}/${wizardStepIds.length} ${s.label}`}
                        className={`rounded-lg border px-2 py-1.5 text-left leading-tight ${
                          wizardStep === s.id
                            ? 'border-brand-sky bg-sky-50 text-sky-950'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <span
                          className={`block text-[9px] font-bold uppercase tracking-wide ${
                            wizardStep === s.id ? 'text-sky-900' : 'text-slate-500'
                          }`}
                        >
                          STEP {stepNum}/{wizardStepIds.length}
                        </span>
                        <span className="mt-0.5 flex items-center gap-0.5 text-[11px] font-semibold">
                          <span>{s.label}</span>
                          {stepHints[s.id] ? <span className="text-rose-600" title="확인 필요">●</span> : null}
                        </span>
                      </button>
                      )
                    })}
                  </div>
                </div>
              ) : null}
            </div>

            {layoutMode === 'wizard' ? (
              <div className="rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2 lg:hidden">
                <p className="text-[11px] font-medium text-slate-700">PC 넓은 화면에서는 왼쪽에 명함 미리보기가 함께 표시됩니다.</p>
              </div>
            ) : null}

            {layoutMode === 'wizard' ? (
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-2">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    STEP {Math.max(0, wizardStepIds.indexOf(wizardStep)) + 1} / {wizardStepIds.length}
                  </div>
                  <div className="text-sm font-bold text-slate-900">
                    {wizardStepDisplayMeta.find((s) => s.id === wizardStep)?.label || '단계'}
                  </div>
                  <div className="mt-0.5 text-[13px] text-slate-500">각 단계에서 「다음 단계로」를 누르면 저장 후 다음 화면으로 이동합니다.</div>
                </div>
                <button
                  type="button"
                  className={`rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:opacity-60 ${
                    wizardStepIds.indexOf(wizardStep) < wizardStepIds.length - 1
                      ? 'bg-slate-800 hover:bg-slate-700'
                      : 'bg-brand-navy hover:bg-brand-navy/95'
                  }`}
                  disabled={updateMutation.isPending}
                  onClick={handleWizardSaveAndNext}
                >
                  {updateMutation.isPending ? '저장 중…' : (wizardStepIds.indexOf(wizardStep) < wizardStepIds.length - 1 ? '다음 단계로' : '저장')}
                </button>
              </div>
            ) : null}

        {/* <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3 ${hid(1)}`}>
          <div className="text-sm font-semibold text-slate-800">명함·홈 연결</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={showLandingLink} onChange={(e) => setShowLandingLink(e.target.checked)} />
              명함에 홈페이지 링크
            </label>
          </div>
        </div> */}

        <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ${hid(1)}`}>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 text-left"
            onClick={() => setTemplateSectionOpen((v) => !v)}
            aria-expanded={templateSectionOpen}
          >
            <span className="text-sm font-semibold text-slate-800">명함 템플릿</span>
            <span className="shrink-0 text-xs font-medium text-slate-500">{templateSectionOpen ? '접기' : '펼치기'}</span>
          </button>
          {templateSectionOpen ? (
            <>
              <div className="mt-3 grid gap-2">
                {CARD_TEMPLATE_OPTIONS.map((item) => (
                  <div
                    key={item.id}
                    className={`flex min-w-0 items-stretch gap-0 overflow-hidden rounded-xl border ${
                      cardTemplateId === item.id
                        ? 'border-brand-sky bg-slate-50/80 ring-1 ring-brand-sky/30'
                        : 'border-slate-100 hover:bg-slate-50/80'
                    }`}
                  >
                    <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-3 px-4 py-3">
                      <input
                        type="radio"
                        name="cardTemplateId"
                        value={item.id}
                        checked={cardTemplateId === item.id}
                        onChange={(e) => setCardTemplateId(e.target.value)}
                        className="mt-1 shrink-0"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-800">{item.label}</div>
                        {/* <div className="mt-1 text-xs text-slate-500">{item.description}</div> */}
                      </div>
                    </label>
                    <div className="flex shrink-0 flex-col justify-center border-l border-slate-200/80 bg-white/60 px-1 py-2">
                      <button
                        type="button"
                        className="rounded-lg px-2.5 py-2 text-center text-[11px] font-semibold text-sky-700 hover:bg-sky-50"
                        onClick={() => setCardSamplePreviewId(item.id)}
                      >
                        샘플 보기
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {/* {cardEditUi.profileImageSizeGuide ? (
                <div className="mt-3 rounded-lg border border-sky-100 bg-sky-50/90 px-3 py-2 text-[11px] leading-snug text-sky-950">
                  <span className="font-semibold text-sky-900">선택한 템플릿 — 프로필 사진 권장 · </span>
                  {cardEditUi.profileImageSizeGuide}
                </div>
              ) : null}
              {cardEditUi.profileExtraHint ? (
                <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-medium leading-snug text-amber-950">
                  <span className="text-amber-900/80">선택한 템플릿 · </span>
                  {cardEditUi.profileExtraHint}
                </div>
              ) : null} */}
            </>
          ) : null}
        </div>

        <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3 ${hid(2)}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold text-slate-800">프로필 사진</div>
            <button
              type="button"
              className="rounded-lg border bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => navigate('/profile')}
            >
              내 정보에서 변경
            </button>
          </div>
          {cardEditUi.profileImageSizeGuide ? (
            <div className="rounded-lg border border-sky-100 bg-sky-50/90 px-3 py-2 text-[11px] leading-snug text-sky-950">
              <span className="font-semibold text-sky-900">권장 이미지 사이즈 :  </span>
              {cardEditUi.profileImageSizeGuide}
            </div>
          ) : null}
          {cardEditUi.profileExtraHint ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-medium leading-snug text-amber-950">
              {cardEditUi.profileExtraHint}
            </div>
          ) : null}
          {profileImageUrl && (
            <button
              type="button"
              className="inline-flex max-w-full items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-left shadow-sm ring-slate-200 transition hover:bg-slate-50 hover:ring-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy/40"
              onClick={() => setImagePreview({ rawUrl: profileImageUrl, title: '프로필 사진' })}
            >
              <img
                src={resolveApiAssetUrl(profileImageUrl)}
                alt=""
                className="h-11 w-11 shrink-0 rounded-full object-cover object-center ring-1 ring-slate-200/80"
                decoding="async"
                loading="lazy"
                onError={(e) => { try { e.currentTarget.style.display = 'none' } catch { } }}
              />
                <span className="min-w-0 text-[12px] font-medium leading-tight text-slate-700">
                  미리보기
                  <span className="mt-0.5 block text-[11px] font-normal text-slate-500">눌러 크게 보기</span>
                </span>
              </button>
          )}
          {!profileImageUrl ? (
            <div className="rounded-lg border border-dashed bg-slate-50 px-3 py-4 text-sm text-slate-500">
              등록된 사진이 없습니다.
            </div>
          ) : null}
        </div>

        {cardEditUi.showOrgLogoEditor ? (
          <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3 ${hid(2)}`}>
            <div className="text-sm font-semibold text-slate-800">조직 로고</div>
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
              onChange={(e) => {
                const f = e?.target?.files?.[0]
                if (!f) return
                uploadOrgLogoMutation.mutate(f)
                try { e.target.value = '' } catch { }
              }}
              disabled={uploadOrgLogoMutation.isPending || updateMutation.isPending}
            />
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[12px] text-slate-600">
                {cardOrgLogoFileId ? <>현재: <span className="font-mono">{cardOrgLogoFileId}</span></> : <>현재: 없음</>}
              </div>
              <button
                type="button"
                className={`rounded-lg border px-3 py-2 text-sm ${cardOrgLogoFileId ? 'hover:bg-slate-50' : 'opacity-40 cursor-not-allowed'}`}
                onClick={() => {
                  if (!cardOrgLogoFileId) return
                  setCardOrgLogoFileId('')
                  updateMutation.mutate({ cardOrgLogoFileId: '' })
                }}
                disabled={!cardOrgLogoFileId || updateMutation.isPending}
              >
                로고 삭제
              </button>
            </div>
            {cardOrgLogoUrl && (
              <button
                type="button"
                className="inline-flex max-w-full items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-left shadow-sm ring-slate-200 transition hover:bg-slate-50 hover:ring-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy/40"
                onClick={() => setImagePreview({ rawUrl: cardOrgLogoUrl, title: '조직 로고' })}
              >
                <img
                  src={resolveApiAssetUrl(cardOrgLogoUrl)}
                  alt=""
                  className="h-11 w-11 shrink-0 rounded-lg bg-slate-50 object-contain object-center p-0.5 ring-1 ring-slate-200/80"
                  decoding="async"
                  loading="lazy"
                  onError={(e) => { try { e.currentTarget.style.display = 'none' } catch { } }}
                />
                <span className="min-w-0 text-[12px] font-medium leading-tight text-slate-700">
                  미리보기
                  <span className="mt-0.5 block text-[11px] font-normal text-slate-500">눌러 크게 보기</span>
                </span>
              </button>
            )}
          </div>
        ) : null}

        <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-4 ${hid(3)}`}>
          <div className="text-sm font-semibold text-slate-800">기본 정보</div>
          {/* <div className="rounded-lg border border-slate-100 bg-slate-50/90 px-3 py-2.5 text-[12px] leading-snug text-slate-700 space-y-1.5">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">명함에 이렇게 보입니다 (아래 입력과 동기)</div>
            <div><span className="text-slate-500">이름 · </span><span className="font-semibold text-slate-900">{d.name || '—'}</span></div>
            {orgChainDisplay ? (
              <div><span className="text-slate-500">조직(계정) · </span>{orgChainDisplay}</div>
            ) : null}
            <div><span className="text-slate-500">회사(표시) · </span>{displayCompanyOnCard}</div>
            <div><span className="text-slate-500">직책(표시) · </span>{displayJobOnCard}</div>
            <div><span className="text-slate-500">주소(표시) · </span><span className="whitespace-pre-line">{displayAddressOnCard}</span></div>
          </div> */}
          {/* <div className="rounded-lg border border-dashed border-slate-200 bg-white px-3 py-2 text-[11px] text-slate-500">
            회사명은 비워 두면 조직·계정에서 자동으로 채워집니다. 입력란에 자동 값이 보이면 「지금 명함에 나가는 문구」이며, 그대로 저장하면 자동 규칙을 유지합니다. 직접 고치면 그 문구로 고정됩니다.
          </div> */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="block text-sm text-slate-600">회사명(명함 표시)</label>
              <input
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                value={companyNameOverride}
                onChange={(e) => setCompanyNameOverride(e.target.value)}
                maxLength={120}
                placeholder={d.companyName ? `자동: ${d.companyName}` : '조직·계정에서 자동'}
              />
            </div>
            <div>
              <label className="block text-sm text-slate-600">직책명(계정 직함과 동기)</label>
              <input
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                maxLength={40}
                placeholder="비우고 저장 시 기본 규칙(팀장 등)"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-slate-600">주소(비우면 가능한 경우 조직에서 자동)</label>
            <textarea
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 min-h-[64px]"
              value={addressText}
              onChange={(e) => setAddressText(e.target.value)}
              maxLength={200}
              placeholder={
                d.addressText && !String(addressText || '').trim()
                  ? `현재 자동·저장값: ${d.addressText}`
                  : '직접 입력하지 않으면, 가능한 경우 조직 정보가 반영됩니다.'
              }
            />
          </div>
          <div>
            <label className="block text-sm text-slate-600">모바일 팩스 연락처(선택)</label>
            <input
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
              value={mobileFaxNumber}
              onChange={(e) => setMobileFaxNumber(e.target.value)}
              maxLength={30}
              placeholder="예) 0504-123-4567"
            />
          </div>
        </div>

        <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-4 ${hid(4)}`}>
          <div className="text-sm font-semibold text-slate-800">
            {cardEditUi.showCareerFreeEditors && (layoutMode !== 'wizard' || showCareerCard || showFreeTextCard)
              ? '소개·경력·태그'
              : cardEditUi.showCardIntroEditor
                ? '소개·태그'
                : '서비스 태그'}
          </div>
          {cardEditUi.showCardIntroEditor ? (
            <>
              {cardEditUi.introBlockBanner ? (
                <div className="rounded-lg border border-indigo-100 bg-indigo-50/90 px-3 py-2 text-[11px] leading-snug text-indigo-950">
                  {cardEditUi.introBlockBanner}
                </div>
              ) : null}
              <div>
                <label className="block text-sm text-slate-600">간단한 소개글(선택)</label>
                <textarea className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 min-h-[72px]" value={introText} onChange={(e) => setIntroText(e.target.value)} maxLength={300} placeholder="있으면 명함·소개에서 이쪽이 소개 문구로 우선합니다. 줄바꿈으로 여러 줄 입력 가능합니다." />
              </div>
            </>
          ) : (
            <p className="text-[11px] leading-snug text-slate-500">
              이 템플릿(프로필사진강조)은 상단 카드에 서비스 태그만 노출됩니다. 소개 문구는 다른 템플릿에서 편집하거나, 개인홈페이지에서 보강할 수 있습니다.
            </p>
          )}
          <div>
            <label className="block text-sm text-slate-600">
              서비스 태그(#로 구분, 예: #보장점검#실손정리, 선택)
              {cardEditUi.serviceTagsLabelExtra}
            </label>
            <input
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
              value={serviceTags}
              onChange={(e) => setServiceTags(e.target.value)}
              maxLength={300}
              placeholder="예) #보장점검#실손정리#노후준비"
            />
            <p className="mt-1 text-[11px] text-slate-500">해시(#)가 없으면 예전처럼 쉼표로 구분한 입력도 그대로 인식합니다.</p>
          </div>
          {cardEditUi.showCareerFreeEditors && (layoutMode !== 'wizard' || showCareerCard || showFreeTextCard) ? (
            <div className="rounded-lg border border-slate-100 bg-slate-50/70 px-3 py-3 space-y-3">
              <div className="text-[11px] font-semibold text-slate-600">경력·자유 소개 카드(허브형)</div>
              {layoutMode !== 'wizard' || showCareerCard ? (
                <>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    {layoutMode !== 'wizard' ? (
                      <input type="checkbox" checked={showCareerCard} onChange={(e) => setShowCareerCard(e.target.checked)} />
                    ) : null}
                    경력 카드 표시
                  </label>
                  <textarea
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 min-h-[72px] text-sm"
                    value={careerText}
                    onChange={(e) => setCareerText(e.target.value)}
                    maxLength={500}
                    placeholder="경력 요약(허브형 명함 카드로 표시)"
                  />
                </>
              ) : null}
              {layoutMode !== 'wizard' || showFreeTextCard ? (
                <>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    {layoutMode !== 'wizard' ? (
                      <input type="checkbox" checked={showFreeTextCard} onChange={(e) => setShowFreeTextCard(e.target.checked)} />
                    ) : null}
                    자유 소개 카드 표시
                  </label>
                  <textarea
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 min-h-[96px] text-sm"
                    value={freeText}
                    onChange={(e) => setFreeText(e.target.value)}
                    maxLength={2000}
                    placeholder="자유 문구(허브형 명함 카드로 표시)"
                  />
                </>
              ) : null}
            </div>
          ) : null}
        </div>

        {cardEditUi.showTimelineYearEditor ? (
          <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3 ${hid(4)}`}>
            <div className="text-sm font-semibold text-slate-800">타임라인 연도·짧은 설명 (타임라인 네이비)</div>
            <p className="text-[11px] leading-snug text-slate-500">
              연도를 2개 이상 입력하면 명함 하단 타임라인에 왼쪽부터 순서대로 표시됩니다. 각 연도 아래에 한 줄 설명(최대 40자)을 넣을 수 있습니다. 2개 미만으로 저장하면 올해 기준 자동 간격이 적용됩니다.
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-1">
                  <label className="block text-[10px] font-medium text-slate-500 text-center">{i + 1}</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1900}
                    max={2100}
                    className="w-full min-w-0 rounded-lg border border-slate-200 px-1 py-1.5 text-center text-sm"
                    value={cardTimelineSlots[i]?.year ?? ''}
                    onChange={(e) => {
                      const base = cardTimelineSlots.length >= 5 ? cardTimelineSlots.map((s) => ({ ...s })) : defaultCardV9TimelineSlots()
                      base[i] = { ...base[i], year: e.target.value }
                      setCardTimelineSlots(base)
                    }}
                    placeholder="연도"
                  />
                  <input
                    type="text"
                    className="w-full min-w-0 rounded-lg border border-slate-200 px-1 py-1 text-center text-[11px]"
                    value={cardTimelineSlots[i]?.label ?? ''}
                    maxLength={40}
                    onChange={(e) => {
                      const base = cardTimelineSlots.length >= 5 ? cardTimelineSlots.map((s) => ({ ...s })) : defaultCardV9TimelineSlots()
                      base[i] = { ...base[i], label: e.target.value }
                      setCardTimelineSlots(base)
                    }}
                    placeholder="설명"
                  />
                </div>
              ))}
            </div>
            <button
              type="button"
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
              onClick={() => setCardTimelineSlots(defaultCardV9TimelineSlots())}
            >
              자동 연도로 채우기
            </button>
          </div>
        ) : null}

        <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-4 ${hid(5)}`}>
          <div className="text-sm font-semibold text-slate-800">활동·상담 지역</div>
          {/* <p className="text-[11px] leading-snug text-slate-500">
            웹 명함 카드에 반영됩니다. 템플릿에 따라 첫 줄이 보조 문구로 쓰일 수 있습니다.
          </p> */}
          <label className="flex items-center gap-2 text-sm text-slate-700">
            {layoutMode !== 'wizard' ? (
              <input type="checkbox" checked={showActivityAreaCard} onChange={(e) => setShowActivityAreaCard(e.target.checked)} />
            ) : null}
            활동·상담 지역 카드 표시
          </label>
          <div>
            <label className="block text-sm text-slate-600">
              활동지역/상담가능지역(선택)
              {cardEditUi.activityLabelExtra}
            </label>
            <textarea className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 min-h-[72px]" value={activityAreaText} onChange={(e) => setActivityAreaText(e.target.value)} maxLength={200} />
          </div>
        </div>

        <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-4 ${hid(6)}`}>
          <div className="text-sm font-semibold text-slate-800">연락·SNS</div>
          <div>
            <label className="block text-sm text-slate-600">카카오 상담 링크(선택)</label>
            <input className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" value={kakaoChatUrl} onChange={(e) => setKakaoChatUrl(e.target.value)} maxLength={300} placeholder="https://open.kakao.com/..." />
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <label className="block text-sm text-slate-600">유튜브(선택)</label>
              <input className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} maxLength={300} placeholder="https://youtube.com/..." />
            </div>
            <div>
              <label className="block text-sm text-slate-600">인스타그램(선택)</label>
              <input className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" value={instagramUrl} onChange={(e) => setInstagramUrl(e.target.value)} maxLength={300} placeholder="https://instagram.com/..." />
            </div>
            <div>
              <label className="block text-sm text-slate-600">블로그(선택)</label>
              <input className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" value={blogUrl} onChange={(e) => setBlogUrl(e.target.value)} maxLength={300} placeholder="https://..." />
            </div>
          </div>
        </div>

        {cardEditUi.showImageCardEditor ? (
          <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3 ${hid(7)}`}>
            <div className="text-sm font-semibold text-slate-800">이미지 카드(일부유형 사용)</div>
            {/* <p className="text-[11px] leading-snug text-slate-500">
            허브형 등 일부 템플릿에서 상단 비주얼로 쓰입니다. 끄면 표시되지 않습니다.
          </p> */}
            {/* <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={imageCardEnabled} onChange={(e) => setImageCardEnabled(e.target.checked)} />
            이미지 카드 사용
          </label> */}
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
              onChange={(e) => {
                const f = e?.target?.files?.[0]
                if (!f) return
                uploadImageCardMutation.mutate(f)
                try { e.target.value = '' } catch { }
              }}
              disabled={uploadImageCardMutation.isPending || updateMutation.isPending}
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-[12px] text-slate-600">
                {imageCardFileId ? <>현재: <span className="font-mono">{imageCardFileId}</span></> : <>현재: 없음</>}
              </div>
              <button
                type="button"
                className={`rounded-lg border px-3 py-2 text-sm ${imageCardFileId ? 'hover:bg-slate-50' : 'opacity-40 cursor-not-allowed'}`}
                onClick={() => {
                  if (!imageCardFileId) return
                  setImageCardFileId('')
                  setImageCardUrl('')
                  setImageCardEnabled(false)
                  updateMutation.mutate({ imageCardFileId: '', imageCardEnabled: false })
                }}
                disabled={!imageCardFileId || updateMutation.isPending}
              >
                이미지 제거
              </button>
            </div>
            {imageCardUrl ? (
              <button
                type="button"
                className="inline-flex max-w-full items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-left shadow-sm ring-slate-200 transition hover:bg-slate-50 hover:ring-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy/40"
                onClick={() => setImagePreview({ rawUrl: imageCardUrl, title: '이미지 카드' })}
              >
                <img
                  src={resolveApiAssetUrl(imageCardUrl)}
                  alt=""
                  className="h-11 w-11 shrink-0 rounded-lg object-cover object-center ring-1 ring-slate-200/80"
                  decoding="async"
                  loading="lazy"
                  onError={(e) => { try { e.currentTarget.style.display = 'none' } catch { } }}
                />
                <span className="min-w-0 text-[12px] font-medium leading-tight text-slate-700">
                  미리보기
                  <span className="mt-0.5 block text-[11px] font-normal text-slate-500">눌러 크게 보기</span>
                </span>
              </button>
            ) : null}
          </div>
        ) : null}

          </div>
        </div>
      </div>

      {reconfigureModal}
      {cardSamplePreviewModal}

      {imagePreview ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="image-preview-title"
          onClick={() => setImagePreview(null)}
        >
          <div
            className="max-h-[min(92vh,880px)] w-full max-w-[min(96vw,920px)] overflow-hidden rounded-2xl border border-white/10 bg-slate-950 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
              <h2 id="image-preview-title" className="truncate text-sm font-semibold text-white">
                {imagePreview.title}
              </h2>
              <button
                type="button"
                className="shrink-0 rounded-lg bg-white/10 px-3 py-1.5 text-sm font-medium text-white hover:bg-white/20"
                onClick={() => setImagePreview(null)}
              >
                닫기
              </button>
            </div>
            <div className="max-h-[calc(min(92vh,880px)-52px)] overflow-auto p-4 sm:p-6">
              <img
                src={resolveApiAssetUrl(imagePreview.rawUrl)}
                alt=""
                className="mx-auto max-h-[min(78vh,720px)] w-auto max-w-full rounded-lg object-contain"
                decoding="async"
                loading="eager"
              />
              <p className="mt-3 text-center text-[11px] text-slate-400">배경을 누르거나 Esc로 닫을 수 있습니다.</p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
